import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFieldArray, useForm } from 'react-hook-form'
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { PATTERNS } from '@/utils/validation'
import { 
  useDestinatario, 
  useCreateDestinatario, 
  useUpdateDestinatario 
} from '@/hooks/useOrdini'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'

const emptyAddress = () => ({
  etichetta: 'Principale',
  via: '',
  civico: '',
  cap: '',
  citta: '',
  provincia: '',
  nazione: 'IT',
  note: '',
  predefinito: true,
})

/**
 * Form creazione/modifica destinatario.
 * 
 * FASE 7.4: Spedizioni Avanzate
 * 
 * Features:
 * - Validazione campi obbligatori (nome o azienda + telefono)
 * - Gestione creazione e modifica
 * - Redirect dopo salvataggio
 */
export default function DestinatarioForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const { data: destinatario, isLoading } = useDestinatario(id)
  const createMutation = useCreateDestinatario()
  const updateMutation = useUpdateDestinatario()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    setValue,
  } = useForm({
    defaultValues: {
      nome: '',
      cognome: '',
      azienda: '',
      telefono: '',
      email: '',
      note: '',
      indirizzi: [emptyAddress()],
    },
  })
  const { fields: indirizziFields, append, remove } = useFieldArray({
    control,
    name: 'indirizzi',
  })

  // Popola form in modifica
  useEffect(() => {
    if (destinatario) {
      reset({
        nome: destinatario.nome || '',
        cognome: destinatario.cognome || '',
        azienda: destinatario.azienda || '',
        telefono: destinatario.telefono || '',
        email: destinatario.email || '',
        note: destinatario.note || '',
        indirizzi: destinatario.indirizzi?.length
          ? destinatario.indirizzi
          : [emptyAddress()],
      })
    }
  }, [destinatario, reset])

  // Watch per validazione custom
  const nome = watch('nome')
  const azienda = watch('azienda')
  const indirizzi = watch('indirizzi') || []

  const setPredefinito = (selectedIndex) => {
    indirizziFields.forEach((_, index) => {
      setValue(`indirizzi.${index}.predefinito`, index === selectedIndex, {
        shouldDirty: true,
      })
    })
  }

  const removeIndirizzo = (index) => {
    const wasDefault = indirizzi[index]?.predefinito
    remove(index)
    if (wasDefault && indirizziFields.length > 1) {
      setTimeout(() => setValue('indirizzi.0.predefinito', true, { shouldDirty: true }), 0)
    }
  }

  const onSubmit = (data) => {
    // Validazione: almeno nome o azienda
    if (!data.nome && !data.azienda) {
      return
    }

    const mutation = isEdit ? updateMutation : createMutation
    const payload = isEdit ? { id, data } : data

    mutation.mutate(payload, {
      onSuccess: () => {
        navigate('/ordini/destinatari')
      },
    })
  }

  if (isLoading && isEdit) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Caricamento...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/ordini/destinatari')}
        >
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? 'Modifica Destinatario' : 'Nuovo Destinatario'}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? 'Aggiorna i dati del destinatario' : 'Crea un nuovo destinatario per le spedizioni'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Anagrafica</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="nome">
                  Nome {!azienda && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="nome"
                  {...register('nome', {
                    validate: (value) => {
                      if (!value && !azienda) {
                        return 'Specificare almeno il nome o l\'azienda'
                      }
                      return true
                    },
                  })}
                  placeholder="Mario"
                />
                {errors.nome && (
                  <p className="text-sm text-destructive">{errors.nome.message}</p>
                )}
              </div>

              {/* Cognome */}
              <div className="space-y-2">
                <Label htmlFor="cognome">Cognome</Label>
                <Input
                  id="cognome"
                  {...register('cognome')}
                  placeholder="Rossi"
                />
              </div>
            </div>

            {/* Azienda */}
            <div className="space-y-2">
              <Label htmlFor="azienda">
                Azienda {!nome && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="azienda"
                {...register('azienda', {
                  validate: (value) => {
                    if (!value && !nome) {
                      return 'Specificare almeno il nome o l\'azienda'
                    }
                    return true
                  },
                })}
                placeholder="XYZ Srl"
              />
              {errors.azienda && (
                <p className="text-sm text-destructive">{errors.azienda.message}</p>
              )}
              <p className="text-sm text-muted-foreground">
                Ragione sociale per destinatari aziendali
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>Indirizzi di consegna</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Scegli l’indirizzo corretto quando crei una spedizione.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ ...emptyAddress(), etichetta: '', predefinito: false })}
              >
                <PlusIcon className="mr-2 h-4 w-4" />
                Aggiungi indirizzo
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {indirizziFields.map((field, index) => (
              <div key={field.id} className="rounded-lg border p-4 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`indirizzo_${index}_predefinito`}
                      checked={Boolean(indirizzi[index]?.predefinito)}
                      onCheckedChange={() => setPredefinito(index)}
                    />
                    <Label htmlFor={`indirizzo_${index}_predefinito`}>Indirizzo predefinito</Label>
                  </div>
                  {indirizziFields.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeIndirizzo(index)}
                      aria-label={`Rimuovi indirizzo ${index + 1}`}
                    >
                      <TrashIcon className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`indirizzi.${index}.etichetta`}>Etichetta</Label>
                  <Input
                    id={`indirizzi.${index}.etichetta`}
                    {...register(`indirizzi.${index}.etichetta`)}
                    placeholder="Es: Casa, Sede, Magazzino"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor={`indirizzi.${index}.via`}>Via/Piazza *</Label>
                    <Input
                      id={`indirizzi.${index}.via`}
                      {...register(`indirizzi.${index}.via`, { required: 'La via è obbligatoria' })}
                    />
                    {errors.indirizzi?.[index]?.via && (
                      <p className="text-sm text-destructive">{errors.indirizzi[index].via.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`indirizzi.${index}.civico`}>Civico</Label>
                    <Input id={`indirizzi.${index}.civico`} {...register(`indirizzi.${index}.civico`)} />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`indirizzi.${index}.cap`}>CAP *</Label>
                    <Input
                      id={`indirizzi.${index}.cap`}
                      {...register(`indirizzi.${index}.cap`, { required: 'Il CAP è obbligatorio' })}
                      maxLength={10}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor={`indirizzi.${index}.citta`}>Città *</Label>
                    <Input
                      id={`indirizzi.${index}.citta`}
                      {...register(`indirizzi.${index}.citta`, { required: 'La città è obbligatoria' })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`indirizzi.${index}.provincia`}>Provincia</Label>
                    <Input
                      id={`indirizzi.${index}.provincia`}
                      {...register(`indirizzi.${index}.provincia`)}
                      maxLength={2}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`indirizzi.${index}.nazione`}>Nazione *</Label>
                    <Input
                      id={`indirizzi.${index}.nazione`}
                      {...register(`indirizzi.${index}.nazione`, { required: 'La nazione è obbligatoria' })}
                      maxLength={2}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor={`indirizzi.${index}.note`}>Note indirizzo</Label>
                    <Input
                      id={`indirizzi.${index}.note`}
                      {...register(`indirizzi.${index}.note`)}
                      placeholder="Citofono, piano, orari..."
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contatti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Telefono */}
            <div className="space-y-2">
              <Label htmlFor="telefono">
                Telefono <span className="text-destructive">*</span>
              </Label>
              <Input
                id="telefono"
                {...register('telefono', {
                  required: 'Il telefono è obbligatorio per la consegna',
                  pattern: {
                    value: PATTERNS.TELEFONO,
                    message: 'Formato telefono non valido',
                  },
                })}
                placeholder="+39 333 1234567"
              />
              {errors.telefono && (
                <p className="text-sm text-destructive">{errors.telefono.message}</p>
              )}
              <p className="text-sm text-muted-foreground">
                Obbligatorio per la consegna
              </p>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...register('email', {
                  pattern: {
                    value: PATTERNS.EMAIL,
                    message: 'Formato email non valido',
                  },
                })}
                placeholder="mario.rossi@email.com"
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Note Consegna</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                {...register('note')}
                placeholder='Es: "Suonare al citofono", "Consegnare al portiere"'
                rows={3}
              />
              <p className="text-sm text-muted-foreground">
                Istruzioni speciali per la consegna
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Azioni */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/ordini/destinatari')}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending
              ? 'Salvataggio...'
              : isEdit
              ? 'Aggiorna'
              : 'Crea Destinatario'}
          </Button>
        </div>
      </form>
    </div>
  )
}
